import DataProvider from './DataProvider';
import {Course} from './CoursesService'

export enum ChatServiceStatus {
    STARTED = 1,
    STOPPED = 2
}

export type User = {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
}

export type ChatMessage = {
    id: number;
    date: string;
    course: Course;
    group: number;
    user: User;
    message: string;
    isMyMessage: boolean;
}

const DEFAULT_RECEIVE_MESSAGE_DELAY = 5000;
const DEFAULT_RECEIVE_STEP_MESSAGES = 25;

class ChatService {

    public static async getContactsByCourseId(courseId: number, startIndex = 0, count = 0) {
        const res = await DataProvider.executeAction('users', 'readChatContacts', {
            filter: [{
                field: 'course',
                operation: 'eq',
                value: courseId
            }],
            startIndex, count
        });
        return res as {data: Array<User>, totalCounter: number};
    }

    public static async getMessagesByCourseId(courseId: number, filters = [] as any[], startIndex = 0, count = 0) {
        const res = await DataProvider.executeAction('chatMessages', 'readAvailable', {
            filter: [{
                field: 'course',
                operation: 'eq',
                value: courseId
            }, ...filters],
            startIndex, count
        });
        return res as {data: Array<ChatMessage>, totalCounter: number};
    }

    public static async sendMessage(data: ChatMessage) {
        const res = await DataProvider.executeAction('chatMessages', 'create', data);
        return res.errorCode as Number;
    }

    serviceStatus = ChatServiceStatus.STOPPED;
    receivingMessageTimer: NodeJS.Timeout | null = null;
    receiveMessageDelay = 0;
    receiveStepMessages = 0;
    courseId = 0;
    messages: Array<ChatMessage> = [];
    notLoadedMessagesCount = 0;
    onReceiveNewMessage: null | ((message: Array<ChatMessage>) => void) = null;

    constructor(courseId = 0, onReceiveNewMessage = (message: Array<ChatMessage>) => {}, 
        receiveStepMessages = DEFAULT_RECEIVE_STEP_MESSAGES, receiveMessageDelay = DEFAULT_RECEIVE_MESSAGE_DELAY){

        this.courseId = courseId;
        this.onReceiveNewMessage = onReceiveNewMessage;
        this.receiveStepMessages = receiveStepMessages;
        this.receiveMessageDelay = receiveMessageDelay;
    }

    start(){
        if (this.serviceStatus == ChatServiceStatus.STARTED) return;
        this.serviceStatus = ChatServiceStatus.STARTED;
        this.updateMessageService();
    }

    stop(){
        if (this.serviceStatus == ChatServiceStatus.STOPPED || !this.receivingMessageTimer) return;
        clearTimeout(this.receivingMessageTimer);
        this.serviceStatus = ChatServiceStatus.STOPPED;
    }

    getServiceStatus(){
        return this.serviceStatus;
    }

    async getContacts(){
        return await ChatService.getContactsByCourseId(this.courseId);
    }

    async sendMessage(message: string) {
        let data = {
            course: { id: this.courseId as Number } as Course,
            message
        } as ChatMessage;
        const sentResult = await ChatService.sendMessage(data);
        if (this.serviceStatus == ChatServiceStatus.STARTED){
            await this.loadLastMessages();
        }
        return sentResult;
    }

    async loadOldMessages(){
        if (this.notLoadedMessagesCount == 0){
            return [];
        }
        try{
            const filters = [{ field: 'date', operation: 'sort', value: false }] as Array<{field: string, operation: string, value: any}>;
            if (this.messages.length > 0){
                filters.push({ field: 'id', operation: 'le', value: this.messages[0].id });
                filters.push({ field: 'id', operation: 'not_in', value: [this.messages[0].id] });
            }
            const oldMessages = await ChatService.getMessagesByCourseId(this.courseId, filters, 0, this.receiveStepMessages);
            this.notLoadedMessagesCount = oldMessages.totalCounter - oldMessages.data.length;
            this.messages = oldMessages.data.concat(this.messages);
            return oldMessages.data;
        } catch (err){
            console.log(err);
            return [];
        }
    }

    private async loadLastMessages(){

        try{
            const filters = [{ field: 'date', operation: 'sort', value: false }] as Array<{field: string, operation: string, value: any}>;
            if (this.messages.length > 0){
                filters.push({ field: 'id', operation: 'ge', value: this.messages[this.messages.length - 1].id });
                filters.push({ field: 'id', operation: 'not_in', value: [this.messages[this.messages.length - 1].id] });
            }
            const newMessages = (await ChatService.getMessagesByCourseId(this.courseId, filters, 0, this.receiveStepMessages));
            if (this.messages.length == 0){
                this.notLoadedMessagesCount = newMessages.totalCounter - newMessages.data.length;
            }
            newMessages.data = newMessages.data.reverse();
            if (newMessages.data.length == 0){
                return;
            }
            if (this.onReceiveNewMessage) this.onReceiveNewMessage(newMessages.data);
            this.messages = this.messages.concat(newMessages.data);
        } catch (err){
            console.log(err);
        }
    }

    private async updateMessageService(){
        try{
            await this.loadLastMessages();
        } catch (err){
            console.log(err);
        } finally {
            if (this.serviceStatus == ChatServiceStatus.STARTED){
                this.receivingMessageTimer = setTimeout(() => this.updateMessageService(), this.receiveMessageDelay);
            }
        }
    }
}

export default ChatService;